import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { buildProviders, Coerce, KalAutocompleteOption, KalDate } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import { BehaviorSubject, merge, Observable, of, Subject, throwError, zip } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import {
    BaseActivityRepaymentSchedule,
    FirstDueDateTypeTypes,
    RepaymentScheduleRow,
    SharedRepaymentSchedule
} from 'src/app/_shared/classes/settlements/shared-repayment-schedule';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/classes/kal-table-header';
import { KalTableComponent } from 'src/app/_shared/modules/kal-table/kal-table.component';
import { ScheduleService } from 'src/app/_shared/services/accounting/schedule.service';
import { UserState } from 'src/app/_user/store/user/user.state';
import { MoyensPaiement, TypeActivite } from 'src/app/board/activity/activity.constants';
import { arrayEmpty, arrayNotEmpty, arrayOfMoreThanOne, compare } from 'src/app/utils/array.utils';
import { getKalDateFromObject } from 'src/app/utils/date.utils';
import { toDoubleDigit } from 'src/app/utils/number.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';
import {
    FactureDto,
    FactureEcheancierModelDto,
    MoyenPaiementDto,
    MutationGetListeBanquesFromMouvementComptableArgs,
    MutationGetListeModeleEcheancieActiviteArgs,
    MutationGetMiniDateDebutLocOrGroupeArgs,
    MutationGetModeleEcheancierInfosArgs,
    ParamModeleEchancierModel,
    RibModelDto,
    SelectionModeleEchancierModel
} from 'src/graphql/generated-types';

type valueType = 'string' | 'number' | 'KalDate' | 'date' | 'boolean';
type FactureType = FactureEcheancierModelDto | FactureDto;

export interface SharedEditRepaymentSchedule {

    title: string;

    paymentMethod: MoyenPaiementDto;

    paymentStartDate: KalDate;

    activityId: number;

    amount: number;

    numberOfDueDate?: number;

    paymentDayOfMonth?: number;

    paymentFrequency?: number;

    repaymentSchedule?: SharedRepaymentSchedule;

    // dans quel mode ouvre t'on cette modale ? Création ou mise à jour d'un échéancier
    editionMode?: 'creation' | 'update';

    // le nom du bénéficiaire auquel est lié cet échéancier
    beneficiary?: string;

    // montant "restant à payer"
    remainingAmount?: number;

    // échéances restantes
    remainingDueDate?: number;

    // liste des factures reliées
    invoicesList?: FactureType[];

    // liste des ribs
    ibansList?: RibModelDto[];

    // id de l'échéancier
    Id?: number;

    // id du bénéficiaire
    IdBenef?: number;

    // id de l'iban
    IdRib?: number;

    // id du compte bancaire
    IdCompteBancaire?: number;

}

const LABEL_MAX_LENGTH = 100;
const CHEQUE_MAX_LENGTH = 20;

@Component({
    selector: 'app-shared-edit-repayment-schedule',
    templateUrl: './shared-edit-repayment-schedule.component.html',
    styleUrls: ['./shared-edit-repayment-schedule.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedEditRepaymentScheduleComponent),
    standalone: false
})
export class SharedEditRepaymentScheduleComponent extends CustomFormControlAccess implements OnInit, OnDestroy {

    // 30 ans max
    static maxNumberOfDueDate = 30 * 12;

    @Input()
    @Coerce('boolean')
    disablePaymentMinStartDateValidator = false;
    @ViewChild('table') tableComponent: KalTableComponent<RepaymentScheduleRow>;
    autocompleteBankNameDataSource: KalAutocompleteOption[] = [];

    form: FormGroup;

    hasFirstDueAmount = false;
    datasource: RepaymentScheduleRow[] = [];
    readonly columns: KalTableHeader<RepaymentScheduleRow>[] = [
        {label: 'Libellé de l\'échéance', code: 'LibelleEcheance'},
        {label: 'Date', code: 'DateEcheance'},
        {label: 'N° de chèque', code: 'NumeroCheque'},
        {label: 'Nom Banque', code: 'NomBanque'},
        {label: 'Nom tiré', code: 'NomTire'},
        {label: 'Remise en banque', code: 'IsRemisEnBanque'},
        {label: 'Montant', code: 'MontantEcheance'},
    ];
    repaymentScheduleModelList: SelectionModeleEchancierModel[] = [];
    readonly invoicesListColumns: KalTableHeader<FactureEcheancierModelDto>[] = [
        {code: 'DateFacture', label: 'Date'},
        {code: 'ReferenceFacture', label: 'Reference Facture'},
        {code: 'MontantFacture', label: 'Montant'},
        {code: 'LibelleFacture', label: 'Libellé'}
    ];
    repaymentSchedule: SharedRepaymentSchedule;

    protected readonly FirstDueDateTypeTypes = FirstDueDateTypeTypes;

    private readonly unsubscribeRepaymentScheduleFormSubject$ = new Subject<boolean>();

    private repaymentScheduleRows: RepaymentScheduleRow[] = [];

    private _editRepaymentSchedule = new BehaviorSubject<SharedEditRepaymentSchedule | null>(null);

    private _loading = new BehaviorSubject(false);

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly fb: FormBuilder,
                private readonly graphqlService: GraphqlService,
                private readonly scheduleService: ScheduleService,
                private readonly store: Store
    ) {
        super();
    }

    get editRepaymentSchedule(): SharedEditRepaymentSchedule | null {
        return this._editRepaymentSchedule.getValue() || null;
    }

    @Input()
    set editRepaymentSchedule(list: SharedEditRepaymentSchedule) {
        this._editRepaymentSchedule.next(list);
    }

    get editRepaymentSchedule$(): Observable<SharedEditRepaymentSchedule> {
        return this._editRepaymentSchedule.asObservable();
    }

    get loading(): boolean {
        return this._loading.getValue();
    }

    set loading(loading: boolean) {
        this._loading.next(loading);
        this.cdr.markForCheck();
    }

    get usingCheque(): boolean {
        return this.editRepaymentSchedule?.paymentMethod?.TypeMoyenPaiement?.Code === MoyensPaiement.ChequeBancaire;
    }

    get minDueDate(): KalDate {
        if (arrayNotEmpty(this.editRepaymentSchedule?.invoicesList)) {
            const sortedInvoice: FactureType = [...this.editRepaymentSchedule.invoicesList].sort(compare('DateFacture'))[0];
            return getKalDateFromObject<FactureType, 'DateFacture'>(sortedInvoice, 'DateFacture');
            // recherche de la date la plus ancienne
        }
        return this.repaymentSchedule.paymentStartDate;
    }

    get displayedColumns(): string[] {
        const columns = this.columns
            // si c'est en update on garde tout sinon on garde tout ce qui n'est pas `IsRemisEnBanque`
            .filter(c => this.isUpdate || c.code !== 'IsRemisEnBanque')
            // si c'est chèque on prend tout, sinon on enlève les colonnes spécifiques aux chèques
            .filter(c => this.usingCheque || ['NumeroCheque', 'NomBanque', 'NomTire'].every(p => p !== c.code));

        return [...new Set(columns.map(c => c.code as string))];
    }

    get repaymentScheduleForm(): FormArray {
        return this.form.get('repaymentSchedule') as FormArray;
    }

    /**
     * total of dueDateAmounts
     * /!\ recalculé trop souvent, si problème de performance il faudrait améliorer ça
     */
    get totalOfDueAmounts(): number {
        return (this.repaymentScheduleRows || [] as RepaymentScheduleRow[])
            // on somme le montant de chacun des échéances
            .reduce((sum, current) => toDoubleDigit(sum + +current.MontantEcheance), 0);
    }

    get hasNoMissingCheckNumber(): boolean {
        return (this.repaymentScheduleForm?.value as RepaymentScheduleRow[]).every(({NumeroCheque}) => !!NumeroCheque);
    }

    get formValid(): boolean {
        return (this.form.valid || !this.scheduleFormGroup.get('ibanId').value)
            // Le formulaire sera valide si :
            && (
                // * On utilise pas les chèques
                !this.usingCheque
                // * Ou Le numéro de chèque n'est pas obligatoire
                || !this.isCheckNumberRequired
                // * Ou si le numéro de chèque est obligatoire, il n'y a pas de numéros manquants
                || this.hasNoMissingCheckNumber
            );
    }

    get minimumDueAmount(): number {
        const {amount} = this.repaymentSchedule || {};
        const firstDueDateAmount = this.repaymentSchedule.getFirstDueDateAmount();

        if (amount === firstDueDateAmount) {
            // Lorsque le montant de la première échéance correspond au montant total
            // Il n'y a qu'une seule échéance correspondant au montant total possible
            return amount;
        }

        const remainingAmount = amount - firstDueDateAmount;
        return toDoubleDigit(remainingAmount > 0
             ? remainingAmount / SharedEditRepaymentScheduleComponent.maxNumberOfDueDate
             : 0);
    }

    get maximumDueAmount(): number {
        const {amount} = this.repaymentSchedule || {};
        const firstDueDateAmount = this.repaymentSchedule.getFirstDueDateAmount();

        if (amount === firstDueDateAmount) {
            // Lorsque le montant de la première échéance correspond au montant total
            // Il n'y a qu'une seule échéance correspondant au montant total possible
            return amount;
        }

        return toDoubleDigit(amount - firstDueDateAmount);
    }

    get maximumNumberOfDueDate(): number {
        const {amount} = this.repaymentSchedule || {};
        const firstDueDateAmount = this.repaymentSchedule.getFirstDueDateAmount();

        if (amount === firstDueDateAmount) {
            // Lorsque le montant de la première échéance correspond au montant total
            // Il ne peut y avoir qu'une seule échéance
            return 1;
        }

        return SharedEditRepaymentScheduleComponent.maxNumberOfDueDate;
    }

    get minimumNumberOfDueDate(): number {
        const {amount} = this.repaymentSchedule || {};
        const firstDueDateAmount = this.repaymentSchedule.getFirstDueDateAmount();

        // Lorsqu'un montant de première échéance défini est inférieur au montant total
        // Le nombre minimum d'échéance est obligatoirement de 2 (Première échéance + complément(s))
        const hasFirstDueDateValue = firstDueDateAmount > 0 && firstDueDateAmount < amount;

        return hasFirstDueDateValue ? 2 : 1;
    }

    get isCheckNumberRequired(): boolean {
        const CheckNumberRequiredParameter = this.store.selectSnapshot(UserState.applicationParameters).find(p => p.CodeChamp === 'CNUMCH');
        return this.usingCheque && CheckNumberRequiredParameter?.CodeValeur === '1';
    }

    get isUpdate(): boolean {
        return this.editRepaymentSchedule?.editionMode === 'update';
    }

    get usingPrevelementOuVirement(): boolean {
        return !this.usingCheque;
    }

    get firstDueDateTypeLabel(): string {
        return this.firstDueDateTypeControl.value === FirstDueDateTypeTypes.PERCENTAGE
           ? 'Pourcentage'
           : 'Montant';
    }

    private get scheduleFormGroup(): FormGroup {
        return this.form?.get('settings.schedule') as FormGroup;
    }

    private get unsubscribeRepaymentScheduleForm$(): Observable<any> {
        return this.unsubscribeRepaymentScheduleFormSubject$.asObservable();
    }

    private get dueDateValidators(): ValidatorFn[] {
        return [
            FormValidator.isNotNull,
            Validators.pattern(new RegExp('^[0-9]+$')),
            Validators.max(this.maximumNumberOfDueDate),
            Validators.min(this.minimumNumberOfDueDate),
        ];
    }

    private get dueDateAmountValidators(): ValidatorFn[] {
        return [
            FormValidator.isNotNull,
            Validators.min(this.minimumDueAmount),
            Validators.max(this.maximumDueAmount)
        ];
    }

    private get firstDueDateValueControl(): FormControl {
        return this.scheduleFormGroup.get('firstDueDateValue') as FormControl;
    }

    private get firstDueDateTypeControl(): FormControl {
        return this.scheduleFormGroup.get('firstDueDateType') as FormControl;
    }

    // convert string to float ( with comma and point ) or preserve date
    private static cleanSettingsFormValue(value: string, type: valueType): number | Date | KalDate | string | boolean {
        if (!value) {
            return null;
        }
        switch (type) {
            case 'boolean':
            case 'string':
                return value;
            case 'date':
                return (value as any as KalDate).toDate();
            case 'KalDate':
                return (value as any as KalDate);
            case 'number':
                value = (value + '').replace(',', '.');
                return parseFloat(value);
            default:
                throw new Error(`${type} is not a cleanable type`);
        }
    }

    validate(c: AbstractControl): Observable<ValidationErrors | null> {
        if(this.isUpdate) {
            return of(this.formValid && this.editRepaymentSchedule.remainingAmount > 0 ? null : {editrepaymentscheduleerror: true});
        }

        return of(this.formValid ? null : {editrepaymentscheduleerror: true});
    }

    toggleFirstDueAmount(): void {
        this.hasFirstDueAmount = !this.hasFirstDueAmount;
        this.cdr.detectChanges();
    }

    focusNextElement(type: string, index: number): void {
        if (index < this.repaymentScheduleRows.length) {
            this.focus(type + (index + 1));
        }
    }

    focusPreviousElement(type: string, index: number): void {
        if (index > 0) {
            this.focus(type + (index - 1));
        }
    }

    patchAutocompletedBank(bank: KalAutocompleteOption): void {
        this.scheduleFormGroup.get('bankName').patchValue(bank.value);
    }

    addRepaymentRow(): void {
        // représente l'index de cette nouvelle ligne dans l'échéancier
        const rowIndex = this.repaymentSchedule.schedule.length - this.repaymentSchedule.alreadyPayedRow.length;
        this.repaymentSchedule.schedule.push({
            LibelleEcheance: `Nouvelle échéance - ${this.repaymentSchedule.label}`,
            DateEcheance: this.repaymentSchedule.getDueDate(rowIndex),
            NomBanque: this.scheduleFormGroup.get('bankName').value,
            NomTire: this.scheduleFormGroup.get('transactionName').value,
            NumeroCheque: this.repaymentSchedule.getChequeNumber(rowIndex),
            IsRemisEnBanque: false,
            MontantEcheance: this.editRepaymentSchedule.amount - this.totalOfDueAmounts
        });
        this.reloadRows();
    }

    deleteRepaymentRow(row: RepaymentScheduleRow): void {
        const index = this.repaymentSchedule.schedule.findIndex(r => r === row);
        this.repaymentSchedule.schedule.splice(index, 1);
        this.reloadRows();
    }

    regenerateLabels(): void {
        const schedulesList = this.repaymentSchedule.schedule
            .filter(term => !term.IsRemisEnBanque)
            .sort((a, b) => a.DateEcheance.isBefore(b.DateEcheance) ? -1 : 1);

        schedulesList.forEach((schedule, index) => {
            schedule.LibelleEcheance = `Échéance ${index + 1}/${schedulesList.length} - ${this.form.get('settings.title').value} [MàJ:${new KalDate()}]`;
        });

        this.updateSchedule(null, false);
    }

    /**
     * Pour régénérer manuellement l'échéancier
     */
    refreshSchedule(): void {
        this.updateSchedule({prop: null, reset: true});
        this.tableComponent?.render();
        this.cdr.markForCheck();
    }

    private reloadRows(): void {
        this.updateSchedule({prop: null}, false);
        this.tableComponent?.render();
        this.cdr.markForCheck();
    }

    private focus(selectorById: string): void {
        const element = document.getElementById(selectorById);
        if (element) {
            element.focus();
        }
    }

    private updateSchedule(params?: { prop: keyof BaseActivityRepaymentSchedule, reset?: boolean }, rebuild = true): void {
        if (rebuild) {
            const rows = this.repaymentSchedule.buildSchedule(params);
            // store for cdkVirtualFor loop
            this.repaymentScheduleRows = rows;
            // donner les nouvelles lignes au tableau virtuel
            this.datasource = rows;
        }

        // est ce qu'on affiche la première échéance ?
        const firstDueDateAmount = this.repaymentSchedule.getFirstDueDateAmount();
        this.hasFirstDueAmount = !!firstDueDateAmount || firstDueDateAmount < 0;

        // Le control est vidé puis reconstruit, ce next permettant de ne plus souscrire aux changements sur le formulaire
        this.unsubscribeRepaymentScheduleFormSubject$.next(null);

        // reconstruire le form array de l'échéancier
        this.repaymentScheduleForm.clear();

        // Suite à l'actualisation du tableau, des doublons peuvent apparaitres et fausser l'échéancier déjà créé. (ex: 3 lignes "Échéance 1/4")
        // Dans ce cas, on supprime ces doublons
        if (arrayOfMoreThanOne(this.repaymentScheduleRows) && this.repaymentScheduleRows.every(r => r.Id > 0 && r.Id === this.repaymentScheduleRows[0].Id)) {
            this.repaymentScheduleRows.splice(1, this.repaymentScheduleRows.length - 1);
        }

        // le push des formGroup un par un dans le formArray est très lent pour beaucoup de lignes
        // alors on recréé complètement le formArray par soucis de rapidité
        this.form.setControl('repaymentSchedule', this.buildRepaymentScheduleForm(this.repaymentScheduleRows));

        this.watch$ = this.form.get('repaymentSchedule').valueChanges.pipe(
            // takeUntil pour ne pas recevoir la value [] lors du clear
            takeUntil(this.unsubscribeRepaymentScheduleForm$),
            tap(rows => {
                // mettre à jour l'échéancier dans l'objet de stockage d'information
                this.repaymentSchedule.updateSchedule(rows);
                // mettre à jour l'échéancier pour l'affichage
                this.repaymentScheduleRows = rows;
                // donner les nouvelles lignes au tableau virtuel
                this.datasource = rows;
            }),
            tap(() => {
                this.cdr.markForCheck();
            })
        );
    }

    /**
     * build repayements schedule form with data
     */
    private buildRepaymentScheduleForm(initialData: RepaymentScheduleRow[]): FormArray {
        return this.fb.array(
            initialData.map(row => {
                const group = new FormGroup({});
                Object.entries(row).forEach(([name, value]) => {
                    const disabled = row.IsRemisEnBanque === true;
                    const validators = [];
                    if (name === 'DateEcheance') {
                        // La date d'échéance ne peut pas être antérieure à la date de facture la plus récente
                        validators.push(FormValidator.minDateValidator(this.minDueDate));
                    }
                    group.addControl(name, new FormControl<RepaymentScheduleRow>({value, disabled}, {updateOn: 'blur', validators}));
                });
                return group;
            }), // initial data
            // je ne met pas le validator ici car c'est trop coûteux en calcul ( appelé 74 fois pour un échéancier de 20 échéances ... )
        );
    }

    private loadBankNames(): void {
        this.watch$ = this.graphqlService.mutateOne<'GetListeBanquesFromMouvementComptable', MutationGetListeBanquesFromMouvementComptableArgs>({
            mutation: gql`
                mutation GetListeBanquesFromMouvementComptable($contexte: ContextInfosInput) {
                    GetListeBanquesFromMouvementComptable(contexte: $contexte) {
                        string
                    }
                }
            `,
        }).pipe(
            take(1),
            tap(data => this.autocompleteBankNameDataSource = get(data, 'string', []).map(d => ({label: d, value: d})))
        );
    }

    private resetDueDateAmountValidators(): void {
        const dueDateAmountControl = this.scheduleFormGroup.get('dueDateAmount') as FormControl;
        dueDateAmountControl.clearValidators();

        dueDateAmountControl.setValidators(this.dueDateAmountValidators);
    }

    /**
     * recrée les validators pour le champs "numberOfDueDate"
     * car les min/max dépendent de valeurs qui changent:
     *  * maximumNumberOfDueDate
     *  * minimumNumberOfDueDate
     */
    private resetNumberOfDueDateValidators(): void {
        const numberOfDueDateControl = this.scheduleFormGroup.get('numberOfDueDate') as FormControl;
        numberOfDueDateControl.clearValidators();

        numberOfDueDateControl.setValidators(this.dueDateValidators);
    }

    private updateForm(): void {
        const schedule = this.repaymentSchedule?.schedule?.[0];
        this.form.patchValue({
            settings: {
                title: this.editRepaymentSchedule?.title,
                schedule: {
                    ibanId: this.editRepaymentSchedule?.IdRib,
                    scheduleType: this.repaymentScheduleModelList.find(m => m.IdModeleEcheancier === this.repaymentSchedule.modelId) || null,
                    numberOfDueDate: this.repaymentSchedule.numberOfDueDate,
                    dueDateAmount: this.repaymentSchedule.dueDateAmount,
                    paymentDayOfMonth: this.repaymentSchedule.paymentDayOfMonth || 1,
                    paymentFrequency: this.repaymentSchedule.paymentFrequency || 1,
                    paymentStartDate: this.repaymentSchedule.paymentStartDate,
                    firstDueDateValue: 0,
                    firstDueDateType: FirstDueDateTypeTypes.EURO,
                    bankName: schedule?.NomBanque || null,
                    transactionName: schedule?.NomTire || null
                }
            }
        });

        // si on est en mode chéquier, on rajoute les deux champs
        if (this.usingCheque) {
            this.scheduleFormGroup.addControl('firstChequeNumber', new FormControl<string>(schedule?.NumeroCheque || null, [
                Validators.maxLength(CHEQUE_MAX_LENGTH),
                FormValidator.number,
                ...(this.isCheckNumberRequired ? [Validators.required] : [])
            ]));
            this.scheduleFormGroup.addControl('useConsecutiveChequeNumber', new FormControl(true));
        }

        // En mode création, on actualise 1 fois le tableau des échéances pour s'assurer des bonnes valeurs affichées !
        if (!this.isUpdate) {
            this.refreshSchedule();
        }
    }

    private getScheduleModelList$(): Observable<SelectionModeleEchancierModel[]> {
        this.loading = true;
        this.repaymentScheduleModelList = [];

        return this.graphqlService.mutateOne<'GetListeModeleEcheancieActivite', MutationGetListeModeleEcheancieActiviteArgs>({
                mutation: gql`
                    mutation GetListeModeleEcheancieActivite($contexte: ContextInfosInput, $criteres: CriteresEcheancierModeleActiviteModelInput) {
                        GetListeModeleEcheancieActivite(contexte: $contexte, criteres: $criteres) {
                            ModeleEchancierListingModel{
                                ... on ParamModeleEchancierModel {
                                    IdModeleEcheancier
                                    Libelle
                                }
                            }
                        }
                    }`,
                variables: {
                    criteres: {
                        IdMoyenPaiement: this.editRepaymentSchedule?.paymentMethod.Id,
                        IsModeleEcheancierClos: false,
                        ListeIdActivite: {
                            decimal: [this.editRepaymentSchedule?.activityId]
                        },
                        MontantReglement: this.editRepaymentSchedule?.amount
                    }
                }
            }
        ).pipe(
            tap(() => this.loading = false),
            switchMap(GetListeModeleEcheancieActivite => {

                if (!GetListeModeleEcheancieActivite) {
                    return of([]);
                }

                this.loading = true;
                return zip(...GetListeModeleEcheancieActivite.ModeleEchancierListingModel.map(e => {
                    return this.graphqlService.mutateOne<'GetModeleEcheancierInfos', MutationGetModeleEcheancierInfosArgs>({
                        mutation: gql`
                                mutation GetModeleEcheancierInfos ($contexte: ContextInfosInput, $idModeleEcheancier: Int) {
                                    GetModeleEcheancierInfos(contexte: $contexte, idModeleEcheancier: $idModeleEcheancier) {
                                        ... on SelectionModeleEchancierModel {
                                            DelaiPremiereEcheance
                                            IdModeleEcheancier
                                            IsDeclenchementAvant
                                            JourDansMois
                                            Libelle
                                            MoisPremiereEcheance
                                            MontantPremiereEcheance
                                            NombreEcheance
                                            NombreJoursDeclenchement
                                            Periodicite
                                            PourcentagePremiereEcheance
                                        }
                                    }
                                }
                            `,
                        variables: {
                            idModeleEcheancier: e.IdModeleEcheancier,
                        }
                    });
                })).pipe(
                    tap(GetModeleEcheancierInfos => {
                        this.repaymentScheduleModelList = GetModeleEcheancierInfos || [];
                        this.loading = false;
                    })
                );
            }),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }

    private getFirstDueDateValidator(type: FirstDueDateTypeTypes): ValidatorFn[] {
        return type === FirstDueDateTypeTypes.PERCENTAGE
               ? [ FormValidator.percent ]
               : [ Validators.min(0), Validators.max(this.repaymentSchedule.amount) ];
    }

    private getMinStartDateLocOrGroup$(invoiceList: number[]): Observable<string> {
        return this.graphqlService.mutateOne<'GetMiniDateDebutLocOrGroupe', MutationGetMiniDateDebutLocOrGroupeArgs>({
            mutation: gql`
                mutation GetMiniDateDebutLocOrGroupe(
                    $contexte: ContextInfosInput
                    $ListeIdFacture: ArrayOfdecimalInput
                ) {
                    GetMiniDateDebutLocOrGroupe(
                        contexte: $contexte
                        ListeIdFacture: $ListeIdFacture
                    )
                }
            `,
            variables: {
                ListeIdFacture: {
                    decimal: invoiceList
                }
            }
        }).pipe(
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }

    private initSubscriptions(): void {
        this.watch$ = this.form.valueChanges.pipe(
            distinctUntilChanged(isEqual),
            tap(() => this.notifyUpdate(this.repaymentSchedule))
        );

        let minDueDateFromLocOrGroup: KalDate | null = null;

        // watch scheduleType updates
        this.watch$ = this.scheduleFormGroup.get('scheduleType').valueChanges.pipe(
            switchMap((model: ParamModeleEchancierModel) => {
                // Dans le cas des activités de type locations et groupes
                // On récupère la date de la première échéance via un appel spécifiue au back en renseignant les factures concernées.
                const filteredInvoiceList = (this.editRepaymentSchedule?.invoicesList || []).filter(
                    (i: FactureDto) => [TypeActivite.Groupe, TypeActivite.Location].includes(i.Activite?.TypeActivite?.Code as TypeActivite)
                );

                if (arrayEmpty(filteredInvoiceList) || !model?.NombreJoursDeclenchement) {
                    return of(model);
                }

                this.loading = true;
                return this.getMinStartDateLocOrGroup$(filteredInvoiceList.map(i => i.Id)).pipe(
                    tap((minDate) => {
                        minDueDateFromLocOrGroup = new KalDate(minDate, environment.date.format.isoQuery);
                        this.loading = false;
                    }),
                    map(() => model)
                );
            }),
            tap((modeleEchancier: ParamModeleEchancierModel) => {
                const sameScheduleModel = modeleEchancier?.IdModeleEcheancier === this.repaymentSchedule?.modelId;

                if (modeleEchancier) {
                    let triggerDaysCount = modeleEchancier?.IsDeclenchementAvant && modeleEchancier?.NombreJoursDeclenchement
                        ? modeleEchancier?.NombreJoursDeclenchement
                        : 0

                    // Cas du déclenchement X jours APRES le début du séjour
                    if (!!modeleEchancier
                        && !modeleEchancier.IsDeclenchementAvant
                        && !!modeleEchancier?.NombreJoursDeclenchement
                        && minDueDateFromLocOrGroup !== null) {
                        triggerDaysCount = -modeleEchancier?.NombreJoursDeclenchement;
                    }

                    modeleEchancier = {
                        ...modeleEchancier,
                        JourDansMois: (minDueDateFromLocOrGroup?.getDay() - triggerDaysCount) || modeleEchancier.JourDansMois,
                        MoisPremiereEcheance: minDueDateFromLocOrGroup?.getMonth() ?? modeleEchancier.MoisPremiereEcheance,
                    }
                    this.repaymentSchedule.updateFromModel(modeleEchancier, minDueDateFromLocOrGroup);
                }
                else {
                    this.repaymentSchedule.modelId = 0;
                }

                this.updateSchedule({prop: null, reset: !sameScheduleModel});
                this.scheduleFormGroup.patchValue(this.repaymentSchedule, {emitEvent: false});

                this.firstDueDateValueControl.clearValidators();
                this.firstDueDateValueControl.setValidators(this.getFirstDueDateValidator(this.firstDueDateTypeControl.value));
                this.firstDueDateValueControl.updateValueAndValidity({emitEvent: false});
            })
        );

        this.watch$ = this.scheduleFormGroup.get('ibanId').valueChanges.pipe(
            tap(ibanId => this.repaymentSchedule.ibanId = ibanId)
        );

        // update du titre de l'échéancier
        this.watch$ = this.form.get('settings.title').valueChanges.pipe(
            tap(title => this.repaymentSchedule.label = title)
        );

        this.watch$ = this.scheduleFormGroup.get('firstDueDateType').valueChanges.pipe(
            distinctUntilChanged(),
            tap((type: FirstDueDateTypeTypes) => {
                this.firstDueDateValueControl.clearValidators();
                this.firstDueDateValueControl.setValidators(this.getFirstDueDateValidator(type));
                this.firstDueDateValueControl.updateValueAndValidity({emitEvent: false});
            })
        );

        // liste des controls qui permettent de contourner la validité du formulaire pour recalculer les échéances
        const bypassableControlsList: (keyof BaseActivityRepaymentSchedule)[] = [
            'numberOfDueDate',
            'dueDateAmount',
            'firstDueDateValue',
            'firstDueDateType',
            'bankName',
            'transactionName',
            'paymentStartDate',
            'paymentDayOfMonth'
        ];

        const fieldsList: Array<{ key: keyof BaseActivityRepaymentSchedule, type: valueType }> = [
            {key: 'numberOfDueDate', type: 'number'},
            {key: 'dueDateAmount', type: 'number'},
            {key: 'paymentDayOfMonth', type: 'number'},
            {key: 'firstDueDateValue', type: 'number'},
            {key: 'firstDueDateType', type: 'string'},
            {key: 'paymentFrequency', type: 'number'},
            {key: 'paymentStartDate', type: 'KalDate'},
            {key: 'transactionName', type: 'string'},
            {key: 'bankName', type: 'string'},
        ];

        if (this.usingCheque) {
            fieldsList.push(
                {key: 'firstChequeNumber', type: 'string'},
                {key: 'useConsecutiveChequeNumber', type: 'boolean'}
            );
        }

        // watch schedule settings updates
        this.watch$ = merge(
            ...fieldsList
                .map(({key, type}) => this.scheduleFormGroup.get(key).valueChanges.pipe(
                    debounceTime(100),
                    map(value => ({
                            key,
                            value: SharedEditRepaymentScheduleComponent.cleanSettingsFormValue(value, type)
                        })
                    )
                ))
        ).pipe(
            distinctUntilChanged(),
            filter(({key, value}: { key: keyof BaseActivityRepaymentSchedule, value: valueType }) =>
                bypassableControlsList.every(property => key !== property || this.repaymentSchedule[property] !== value)
            ),
            tap(<T extends keyof BaseActivityRepaymentSchedule>({
                                                                    key,
                                                                    value
                                                                }: { key: T, value: BaseActivityRepaymentSchedule[T] }) => {
                // est ce que la variable `key` est comprise dans le tableau `relatedControlsList`
                const isFormValidityBypassable = bypassableControlsList.includes(key);

                // limiter pour les performances à 30 ans ( 360 mois )
                // Lorsque 'numberOfDueDate' ou 'dueDateAmount' est modifié, validityOnRelatedControls
                // permet de recalculer le control associé ('numberOfDueDate' si key === 'dueDateAmount')
                // même si le control associé était en erreur

                // dans le cas de la modification des informations de chèques,
                // on ne veut pas recalculer tout l'échéancier, alors on fait un soft update
                // pour éviter d'écraser les modifs qui auraient été réalisées sur l'échéancier
                if (key === 'useConsecutiveChequeNumber') {
                    this.repaymentSchedule.useConsecutiveChequeNumber = value as boolean;
                    // mise à jour de l'échéancier
                    this.scheduleFormGroup.get('firstChequeNumber').patchValue(this.scheduleFormGroup.get('firstChequeNumber').value);
                } else if ((['bankName', 'transactionName', 'firstChequeNumber'] as T[]).includes(key)) {
                    if (key === 'firstChequeNumber') {
                        this.repaymentSchedule.firstChequeNumber = value as string;
                    }
                    const transform: Partial<Record<keyof BaseActivityRepaymentSchedule, any>> = {
                        bankName: {key: 'NomBanque', fn: v => v},
                        transactionName: {key: 'NomTire', fn: v => v},
                        firstChequeNumber: {
                            key: 'NumeroCheque',
                            fn: (v, index) => this.repaymentSchedule.getChequeNumber(index)
                        },
                    };
                    // light rebuild
                    const repaymentScheduleRows = this.repaymentScheduleForm.getRawValue() as RepaymentScheduleRow[];
                    // on met à jour la propriété `key` de chacune des lignes
                    let removedIndex = 0;
                    const updatedRows = repaymentScheduleRows.map((row, index) => {
                        if (row.IsRemisEnBanque) {
                            removedIndex++;
                            return row;
                        }
                        return ({
                            ...row,
                            [transform[key].key]: transform[key].fn(value, index - removedIndex)
                        });
                    });

                    // Workaround #56491
                    // Si toutes les lignes de l'échéancier sont remise en banque, on a un soucis d'affichage en doublon.
                    // On clear le form array en premier lieu afin d'y ajouter toutes les lignes à nouveau
                    if (this.repaymentScheduleRows.every(e => e.IsRemisEnBanque)) {
                        this.repaymentScheduleForm.clear({emitEvent: false});
                    }

                    this.repaymentScheduleForm.patchValue(updatedRows);

                    this.updateSchedule({prop: key}, false);

                }
                // Il ne faut pas regénérer les échéances si une données est mauvaise
                // par exemple : si l'échéance dépasse le montant de la facture
                else if (this.scheduleFormGroup.get(key).valid) {
                    // Si on modifie le type (% ou €) et que la valeur n'est pas valid on ne dois pas continuer.
                    if (key === 'firstDueDateType' && (this.firstDueDateValueControl.invalid || this.firstDueDateValueControl.value === 0)) {
                        return;
                    }
                    // update échéancier
                    this.repaymentSchedule.update(key, value);

                    this.updateSchedule({prop: key}, false);
                    // update form
                    const updatedValues = {...this.repaymentSchedule};
                    // do not patch input which triggered the update
                    delete updatedValues[key];

                    this.scheduleFormGroup.patchValue(updatedValues, {emitEvent: false});

                    if (isFormValidityBypassable) {
                        this.resetDueDateAmountValidators();
                        this.resetNumberOfDueDateValidators();
                        bypassableControlsList.forEach(c => this.scheduleFormGroup.get(c).updateValueAndValidity());
                        this.cdr.markForCheck();
                    }

                }
            }),
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            settings: this.fb.group({
                title: [null, [FormValidator.isNotNull, Validators.maxLength(LABEL_MAX_LENGTH)]],
                schedule: this.fb.group({
                    ibanId: [null],
                    scheduleType: [null, []],
                    numberOfDueDate: null,
                    dueDateAmount: [null, {updateOn: 'blur'}],
                    paymentDayOfMonth: null,
                    paymentFrequency: [null, [FormValidator.isNotNull, Validators.min(1)]],
                    paymentStartDate: null,
                    firstDueDateValue: 0,
                    firstDueDateType: [FirstDueDateTypeTypes.EURO],
                    bankName: null,
                    firstChequeNumber: null,
                    useConsecutiveChequeNumber: false,
                    transactionName: ''
                }),
            }),
            repaymentSchedule: this.buildRepaymentScheduleForm([])
        });

        this.initSubscriptions();

        this.watch$ = this.editRepaymentSchedule$.pipe(
            tap(() => {
                this.repaymentSchedule = this.editRepaymentSchedule.repaymentSchedule || new SharedRepaymentSchedule({
                    amount: this.editRepaymentSchedule.amount,
                    paymentFrequency: this.editRepaymentSchedule.paymentFrequency || 1,
                    paymentDayOfMonth: this.editRepaymentSchedule.paymentDayOfMonth || 1,
                    paymentStartDate: this.editRepaymentSchedule.paymentStartDate || new KalDate(),
                    numberOfDueDate: Math.min(
                        this.editRepaymentSchedule.numberOfDueDate || 1,
                        SharedEditRepaymentScheduleComponent.maxNumberOfDueDate
                    ),
                    useConsecutiveChequeNumber: true
                });

                this.repaymentSchedule.label = this.editRepaymentSchedule?.title;

                // build schedule first

                // Fix FWA: Définition des validateurs dépendants de this.repaymentSchedule pour le formulaire principal
                const validatorList = {
                    firstDueDateValue: [Validators.max(this.repaymentSchedule.amount), Validators.min(0)],
                    numberOfDueDate: this.dueDateValidators,
                    dueDateAmount: this.dueDateAmountValidators,
                    paymentStartDate: [FormValidator.date]
                };

                if (!this.disablePaymentMinStartDateValidator) {
                    validatorList.paymentStartDate.push(FormValidator.minDateValidator(this.minDueDate));
                }

                Object.keys(validatorList).forEach(formControlName => {
                    this.scheduleFormGroup.get(formControlName).setValidators(validatorList[formControlName]);
                });

                // Récupération des noms de banques
                if (this.usingCheque) {
                    this.loadBankNames();
                }

            }),
            // récupération des données nécessaires à l'affichage de la page
            switchMap(() => this.getScheduleModelList$()),
            tap(() => this.updateForm())
        );


    }

    ngOnDestroy(): void {
        this.unsubscribeRepaymentScheduleFormSubject$.complete();
        super.ngOnDestroy();
    }
}
